import { oc } from "@orpc/contract";
import * as z from "zod";
import {
  CommercialAccessSchema,
  CommercialOrderSchema,
  CommercialPlanInputSchema,
  CommercialPlanPublicSchema,
  CommercialPlanSchema,
  PaymentProviderIdSchema,
  PlatformOrderSchema,
  PlatformPermissionSchema,
  PlatformRoleSchema,
} from "./commercial.js";
import { ComputeNodeSchema, NodeEnrollmentInputSchema } from "./compute-nodes.js";
import { IntegrationProviderConfigSchema } from "./integration-settings.js";
import {
  PlatformAuditKindSchema,
  PlatformRequestUsageSchema,
  PlatformUserRefSchema,
} from "./platform-admin.js";
import {
  PlatformModelBillingSchema,
  PlatformModelCatalogSchema,
  PlatformModelDefaultInputSchema,
  PlatformModelEntryInputSchema,
  PlatformModelInputSchema,
  PlatformModelRoutingInputSchema,
  PlatformModelViewSchema,
} from "./platform-model.js";
import { PlatformVoiceEntrySchema, PlatformVoiceInputSchema } from "./platform-voice.js";
import {
  PlatformWebInputSchema,
  PlatformWebRequestContentSchema,
  PlatformWebRequestFilterSchema,
  PlatformWebRequestPageSchema,
  PlatformWebRequestSchema,
  PlatformWebViewSchema,
} from "./platform-web.js";
import {
  RunControlInputSchema,
  RunProgressSnapshotSchema,
  RunTaskSummarySchema,
} from "./run-control.js";

export const dreambotContract = {
  commercial: {
    access: oc.output(CommercialAccessSchema),
    plans: oc.output(z.array(CommercialPlanPublicSchema)),
    orders: oc.output(z.array(CommercialOrderSchema)),
    checkout: oc
      .input(
        z.object({
          planId: z.string(),
          reset: z.boolean().optional(),
          clientNonce: z.string().uuid(),
          /** Defaults to the first configured provider. */
          provider: PaymentProviderIdSchema.optional(),
          /** A phone browser that can hand off to the payment app instead of scanning a code. */
          mobile: z.boolean().optional(),
        }),
      )
      .output(CommercialOrderSchema),
    refresh: oc.input(z.object({ orderId: z.string() })).output(CommercialOrderSchema),
  },
  runControl: {
    list: oc
      .input(
        z
          .object({ botId: z.string().optional(), groupId: z.string().optional() })
          .refine(
            (input) => Boolean(input.botId) !== Boolean(input.groupId),
            "One chat target is required",
          ),
      )
      .output(z.array(RunTaskSummarySchema)),
    progress: oc.input(z.object({ runId: z.string() })).output(RunProgressSnapshotSchema),
    command: oc
      .input(RunControlInputSchema)
      .output(z.object({ version: z.number().int(), state: z.string() })),
  },
  platform: {
    webConfiguration: oc.output(PlatformWebViewSchema),
    saveWebConfiguration: oc
      .input(PlatformWebInputSchema)
      .output(z.object({ ok: z.literal(true) })),
    webRequests: oc.input(PlatformWebRequestFilterSchema).output(
      PlatformWebRequestPageSchema.extend({
        items: z.array(PlatformWebRequestSchema.extend({ user: PlatformUserRefSchema })),
      }),
    ),
    webRequestContent: oc
      .input(z.object({ id: z.string().min(1).max(100) }))
      .output(PlatformWebRequestContentSchema),
    voice: oc.output(z.array(PlatformVoiceEntrySchema)),
    saveVoice: oc.input(PlatformVoiceInputSchema).output(z.object({ ok: z.literal(true) })),
    permissions: oc.output(z.array(PlatformPermissionSchema)),
    model: oc.output(PlatformModelViewSchema),
    models: oc.output(PlatformModelCatalogSchema),
    saveModelEntry: oc
      .input(PlatformModelEntryInputSchema)
      .output(z.object({ ok: z.literal(true), id: z.string() })),
    saveModel: oc.input(PlatformModelInputSchema).output(z.object({ ok: z.literal(true) })),
    saveModelRouting: oc
      .input(PlatformModelRoutingInputSchema)
      .output(z.object({ ok: z.literal(true) })),
    setDefaultModel: oc
      .input(PlatformModelDefaultInputSchema)
      .output(z.object({ ok: z.literal(true) })),
    settings: oc.output(
      z.object({
        enabled: z.boolean(),
        salesEnabled: z.boolean(),
        destructivePurgeEnabled: z.boolean(),
        deletionPolicyVersion: z.string().nullable(),
        refundPolicyVersion: z.string().nullable(),
      }),
    ),
    saveSettings: oc
      .input(
        z.object({
          salesEnabled: z.boolean(),
          destructivePurgeEnabled: z.boolean(),
          deletionPolicyVersion: z.string().min(1).nullable(),
          refundPolicyVersion: z.string().min(1).nullable(),
        }),
      )
      .output(z.object({ ok: z.literal(true) })),
    cancelEnrollment: oc
      .input(z.object({ nodeId: z.string() }))
      .output(z.object({ ok: z.literal(true) })),
    retryEnrollment: oc
      .input(
        z.object({ nodeId: z.string(), credential: NodeEnrollmentInputSchema.shape.credential }),
      )
      .output(z.object({ ok: z.literal(true) })),
    users: oc.output(
      z.array(
        z.object({
          id: z.string(),
          name: z.string(),
          email: z.string(),
          createdAt: z.string(),
          state: z.string(),
          expiresAt: z.string().nullable(),
          role: PlatformRoleSchema.nullable(),
          suspended: z.boolean(),
        }),
      ),
    ),
    suspendUser: oc
      .input(z.object({ userId: z.string(), suspended: z.boolean() }))
      .output(z.object({ ok: z.literal(true) })),
    setRole: oc
      .input(z.object({ userId: z.string(), role: PlatformRoleSchema.nullable() }))
      .output(z.object({ ok: z.literal(true) })),
    plans: oc.output(z.array(CommercialPlanSchema.extend({ active: z.boolean() }))),
    savePlan: oc
      .input(z.object({ plan: CommercialPlanInputSchema, active: z.boolean() }))
      .output(z.object({ ok: z.literal(true) })),
    orders: oc.output(z.array(PlatformOrderSchema)),
    nodes: oc.output(z.array(ComputeNodeSchema)),
    setNodeStorageOversell: oc
      .input(z.object({ nodeId: z.string(), percent: z.number().int().min(100).max(300) }))
      .output(z.object({ ok: z.literal(true) })),
    enrollNode: oc.input(NodeEnrollmentInputSchema).output(z.object({ nodeId: z.string() })),
    drainNode: oc.input(z.object({ nodeId: z.string() })).output(z.object({ ok: z.literal(true) })),
    resumeNode: oc
      .input(z.object({ nodeId: z.string() }))
      .output(z.object({ ok: z.literal(true) })),
    computers: oc.output(
      z.array(
        z.object({
          id: z.string(),
          userId: z.string(),
          user: PlatformUserRefSchema,
          nodeId: z.string().nullable(),
          nodeName: z.string().nullable(),
          state: z.string(),
          workspaceGeneration: z.number().int(),
        }),
      ),
    ),
    incidents: oc.output(
      z.array(
        z.object({
          id: z.string(),
          kind: z.string(),
          state: z.string(),
          summary: z.string(),
          resourceId: z.string(),
        }),
      ),
    ),
    acknowledgeIncident: oc
      .input(z.object({ id: z.string() }))
      .output(z.object({ ok: z.literal(true) })),
    requests: oc
      .input(
        z
          .object({
            /** User ID, email, or name; matched exactly. */
            user: z.string().trim().max(200).optional(),
            runId: z.string().max(100).optional(),
            status: z
              .enum([
                "queued",
                "leased",
                "running",
                "waiting_input",
                "waiting_takeover",
                "completed",
                "failed",
                "cancelled",
              ])
              .optional(),
            beforeId: z.string().max(100).optional(),
          })
          .default({}),
      )
      .output(
        z.array(
          z.object({
            id: z.string(),
            userId: z.string(),
            user: PlatformUserRefSchema,
            status: z.string(),
            modelProvider: z.string().nullable(),
            modelId: z.string().nullable(),
            platformModelCategory: z.string().nullable().optional(),
            modelRoutingOutcome: z.string().nullable().optional(),
            modelRoutingConfidence: z.number().nullable().optional(),
            createdAt: z.string(),
            startedAt: z.string().nullable().optional(),
            completedAt: z.string().nullable().optional(),
            executionKind: z.string().optional(),
            firstResponseAt: z.string().nullable(),
            /** Null when there are no model calls for the request. */
            usage: PlatformRequestUsageSchema.nullable(),
            /** Ledger charge in micros; null without billing.read or when the request has no ledger entries. */
            chargeMicros: z.string().nullable(),
            pendingCharge: z.boolean(),
            /** Null without requests.content. */
            error: z.string().nullable(),
          }),
        ),
      ),
    requestCalls: oc.input(z.object({ runId: z.string().min(1).max(100) })).output(
      z.array(
        z.object({
          id: z.string(),
          callId: z.string().nullable(),
          providerRequestId: z.string().nullable(),
          provider: z.string().nullable(),
          model: z.string().nullable(),
          amountMicros: z.string(),
          pending: z.boolean(),
          priceVersion: z.number().nullable(),
          platformModelEntryId: z.string().nullable().optional(),
          reconciliationState: z.string().nullable(),
          billing: PlatformModelBillingSchema.nullable().optional(),
          createdAt: z.string(),
        }),
      ),
    ),
    audit: oc
      .input(
        z
          .object({
            resourceId: z.string().max(100).optional(),
            beforeId: z.string().max(100).optional(),
            kind: PlatformAuditKindSchema.default("change"),
          })
          .default({ kind: "change" }),
      )
      .output(
        z.array(
          z.object({
            id: z.string(),
            actor: PlatformUserRefSchema,
            action: z.string(),
            resourceId: z.string(),
            /** Set when the resource is a user account. */
            resourceUser: PlatformUserRefSchema,
            createdAt: z.string(),
          }),
        ),
      ),
    integrations: oc
      .input(IntegrationProviderConfigSchema)
      .output(z.object({ ok: z.literal(true) })),
  },
};
